import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Alert, AlertService, AlertType } from '@michelin/theme';
import { Store } from '@ngxs/store';
import _ from 'lodash-es';
import { Observable, Subject, takeUntil } from 'rxjs';
import { emptySpecificationLine } from 'src/app/features/asset-management/models/SpecLine';
import { Specification } from 'src/app/features/asset-management/models/Specification';
import {
  AssetManagementState,
  FetchCurrentSpecificationAction,
  UpdateCurrentSpecificationAction,
  UpdateEditingModeAction
} from 'src/app/shared/state/AssetManagementState';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { PdfExportService } from '../../../../shared/services/pdf-export.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { AssetManagementService, UpdateSpecDescRequest } from '../../../asset-management/services/asset-management.service';
import { UtilityService } from '../../../../shared/services/utility.service';
import { canEditCamSpec } from '../../utils/SpecificationUtils';
import { Dealer } from '../../../../shared/dealer/models/dealer';

@Component({
  selector: 'app-view-edit-cam-spec',
  templateUrl: './view-edit-cam-spec.component.html',
  styleUrls: ['./view-edit-cam-spec.component.scss'],
  standalone: false
})
export class ViewEditCamSpecComponent implements OnInit, OnDestroy {
  private readonly ngUnsubscribe = new Subject<void>();
  loading$: Observable<boolean>;
  specification$: Observable<Specification>;

  specification: Specification | undefined;
  camSpecificationForm: FormGroup;

  editMode = false;
  textFieldClass = 'view-component';
  loading = true;
  isActive = false;
  isEditCamDesciptionEnabled = false;
  hasEditPermission = true;

  private readonly michelinConstant = 'MICHELIN';
  private readonly nafaConstant = 'NAFA';

  public router = inject(Router);
  public route = inject(ActivatedRoute);
  public fb = inject(FormBuilder);
  public translationService = inject(TranslationService);
  public store = inject(Store);
  public assetManagementService = inject(AssetManagementService);
  public alertService = inject(AlertService);
  public pdfExportService = inject(PdfExportService);
  public utilityService = inject(UtilityService);

  constructor() {
    this.camSpecificationForm = this.fb.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      type: ['LOCAL', Validators.required]
    });
  }

  ngOnInit(): void {
    this.loading$ = this.store.select(AssetManagementState.loading);
    this.specification$ = this.store.select(AssetManagementState.currentSpecification);
    this.route.params.subscribe((params) => {
      const specificationId = params['specificationId'];
      this.store.dispatch(new FetchCurrentSpecificationAction(specificationId));

      this.specification$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((specification) => {
        if (specification && specificationId === specification.id) {
          this.specification = _.cloneDeep(specification);
          this.camSpecificationForm.patchValue(this.specification);
          this.loading = false;

          // Auto-select the first line when no line is selected yet. This must
          // live here (re-run on every params emission) rather than in
          // ngAfterViewInit: Angular's default route-reuse strategy reuses this
          // same component instance when navigating directly between two specs
          // that match the same route config (e.g. clicking a notification link
          // while already on this route with a different id/child route active),
          // so a lifecycle hook that only fires once per instance - like
          // ngAfterViewInit - would silently never run for that navigation,
          // leaving the line-detail pane blank.
          const specificationLineId = this.route.snapshot.firstChild?.params['specificationLineId'];
          if (!specificationLineId) {
            const firstLine = this.specification.specificationLines?.[0];
            if (firstLine) {
              this.router.navigate(['lines', firstLine.id], { relativeTo: this.route });
            } else {
              console.warn('CAM spec has no specification lines to display:', specificationId);
            }
          }

          // check if the logged-in user has cam edit privilege
          // If user has access to edit then display below options on view cam specs screen
          // Add lines to existing cams and modify cam spec name
          // user with cam edit privilege still should not be able to edit Michelin and NAFA cams.
          // Only MRT dealer users can edit NAFA cams.
          const dealer: Dealer = this.store.selectSnapshot(DealerState.currentDealer);
          this.hasEditPermission = canEditCamSpec(
            this.michelinConstant === this.specification.type,
            this.nafaConstant === this.specification.type,
            dealer
          );
        }
      });

      const paginatedSpecifications = this.store.selectSnapshot(AssetManagementState.currentPaginatedSpecifications);
      const searchSpecification = paginatedSpecifications.find((spec) => spec.id === specificationId);
      // searchSpecification is undefined when navigating here directly (e.g. from a
      // notification link) without first performing a search - treat that the same
      // as "no search context" rather than throwing.
      this.isActive = paginatedSpecifications.length === 0 || !searchSpecification || searchSpecification.status === 'Active';

      this.store
        .select((state) => state.currentCustomerAssets.editing)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((editMode: boolean) => {
          this.editMode = editMode;
        });
    });
  }

  export() {
    // export feature
    this.pdfExportService.generatePDF(this.specification.name, this.specification.specificationLines, false);
  }

  addLineButtonClick() {
    // add a line to cam spec
    this.store.dispatch(new UpdateEditingModeAction(true));
    const lineId = this.utilityService.generateUuid();
    const specificationLine = emptySpecificationLine(lineId, 'New Line');
    this.specification.specificationLines = [...this.specification.specificationLines];
    this.specification.specificationLines.unshift(specificationLine);
    this.store.dispatch(new UpdateCurrentSpecificationAction(this.specification));
    this.router.navigate(['lines', lineId], { queryParams: { addNewLine: true }, relativeTo: this.route });
  }

  specificationLink(lineId: string) {
    return `/home/specifications/${this.specification.id}/lines/${lineId}`;
  }

  disable_key_sidebar(event: any) {
    if (this.editMode === true) {
      event.preventDefault();
      return false;
    }
  }
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  onDescEditClick() {
    this.isEditCamDesciptionEnabled = true;
  }

  onCancelDescEditClick() {
    this.camSpecificationForm.patchValue(this.specification);
    this.isEditCamDesciptionEnabled = false;
  }

  onDescSaveClick() {
    const updatedValue = this.camSpecificationForm.controls['description'].value;
    const username = this.store.selectSnapshot(DealerState.currentUser);
    const spec = { ...this.specification };
    spec.description = updatedValue;
    let requestObject: UpdateSpecDescRequest;
    requestObject = { description: updatedValue, modifiedBy: username };
    this.assetManagementService.updateSpecDesc(spec.id, requestObject).subscribe(
      () => {
        this.store.dispatch(new UpdateCurrentSpecificationAction(spec));
        this.alertService.showAlert({
          type: AlertType.success,
          title: 'Success',
          content: 'Description updated successfully.'
        } as Alert);
        this.camSpecificationForm.patchValue(spec);
      },
      (error) => {
        const statusCode: number = error.error.statusCode;
        const alertType = 400 <= statusCode && statusCode < 500 ? AlertType.strong : AlertType.critical;
        this.alertService.showAlert({
          type: alertType,
          title: 'Critical Error',
          content: 'Please try again. Something went wrong.'
        } as Alert);
      }
    );
    this.isEditCamDesciptionEnabled = false;
  }
}
